import React from 'react';
import { getHighlightColor } from '../../../common/search/realestate';
import type { RealestateAd, RealestateProjectAd } from '../../../types';
import { SearchAd } from '../SearchAd';
import { useSearchAdClasses } from '../SearchAdClasses';
import type { BaseSearchAdProps } from '../SearchAdTypes';
import { RealestateBullets } from './RealestateBullets';
import { RealestateHeading } from './RealestateHeading';
import { RealestateImage } from './RealestateImage';
import { RealestateLabel } from './RealestateLabel';
import { RealestateLocation } from './RealestateLocation';
import { RealestateOrganisation } from './RealestateOrganisation';
import { RealestateSummary } from './RealestateSummary';
import { ViewingTimes } from './RealestateViewing';

const classes = {
    mobile: {
        imageAspect: 'aspect-16/9 s-bg',
        content: 'p-16 grid',
        organisation: 'col-span-2',
        heading: 'col-span-2 mt-12',
        location: 'mt-4 sf-line-clamp-2',
        summary: 'col-span-2 mt-16 flex justify-between',
        details: 'col-span-2',
        bullets: 'flex flex-col mt-4',
        viewingTimes: 'mt-8 shrink-0',
        ribbon: 'absolute top-0 left-0 pointer-events-none',
    },
    desktop: {
        content: 'sm:grid-cols-2',
        organisation: 'sm:col-span-1 sm:order-first',
        heading: 'sm:mt-24',
        location:
            'sm:order-first sm:text-right sm:mt-0 sm:ml-16 sf-realestate-location',
        summary: 'sm:mt-4 sm:block',
        details: 'sm:flex sm:items-baseline sm:justify-between',
        bullets: 'sm:block sm:mt-8',
        viewingTimes: 'sm:ml-16',
    },
};

const contentStyle: React.CSSProperties = {
    gridTemplateColumns: '1fr max-content',
};

type PremiumRealestateSearchAdProps = BaseSearchAdProps & {
    ad: RealestateAd | RealestateProjectAd;
    footer?: () => React.ReactElement | null;
    imageSizes?: string;
    suppressOrganisationLogo?: boolean;
};

export function PremiumRealestateSearchAd(
    props: PremiumRealestateSearchAdProps,
) {
    const {
        ad,
        className = '',
        footer,
        heading,
        image,
        imageSizes,
        label,
        suppressOrganisationLogo,
        ...searchAdProps
    } = props;

    const highlightStyle: React.CSSProperties = React.useMemo(
        () => ({
            ...contentStyle,
            borderTopWidth: '4px',
            borderColor: getHighlightColor(ad),
        }),
        [ad],
    );

    const c = useSearchAdClasses({
        mode: searchAdProps.mode,
        mobileClasses: classes.mobile,
        desktopClasses: classes.desktop,
    });

    return (
        <SearchAd adId={ad.id} className={className} {...searchAdProps}>
            <RealestateImage
                ad={ad}
                aspectClass={c.imageAspect}
                image={image}
                sizes={imageSizes}
            />
            <div className={c.content} style={highlightStyle}>
                <RealestateOrganisation
                    ad={ad}
                    className={c.organisation}
                    suppressLogo={suppressOrganisationLogo}
                />
                <RealestateHeading
                    ad={ad}
                    className={c.heading}
                    heading={heading}
                />
                <RealestateLocation ad={ad} className={c.location} />
                <RealestateSummary ad={ad} className={c.summary} />
                <div className={c.details}>
                    <RealestateBullets ad={ad} className={c.bullets} />
                    <ViewingTimes ad={ad} className={c.viewingTimes} />
                </div>
            </div>
            <RealestateLabel ad={ad} className={c.ribbon} label={label} />
            {footer && footer()}
        </SearchAd>
    );
}
